import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from '../../icons';
import { IkigaiAnswers, IkigaiFormStep, IKIGAI_STEPS, STEP_ORDER } from './types';

interface IkigaiFormProps {
  initialAnswers?: Partial<IkigaiAnswers>;
  onComplete: (answers: IkigaiAnswers) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

const emptyAnswers = (): IkigaiAnswers => ({
  love_timeflies: '',
  love_energy: '',
  love_unpaid: '',
  goodat_help: '',
  goodat_others: '',
  goodat_learn: '',
  world_problem: '',
  world_change: '',
  world_for: '',
  paid_earned: '',
  paid_value: '',
  paid_market: '',
});

const CONTENT_STEPS: IkigaiFormStep[] = ['love', 'goodat', 'world', 'paid'];

const IkigaiForm: React.FC<IkigaiFormProps> = ({
  initialAnswers,
  onComplete,
  onCancel,
  isSaving = false,
}) => {
  const [currentStep, setCurrentStep] = useState<IkigaiFormStep>('intro');
  const [answers, setAnswers] = useState<IkigaiAnswers>({
    ...emptyAnswers(),
    ...initialAnswers,
  });

  const currentStepIndex = STEP_ORDER.indexOf(currentStep);
  const totalContentSteps = CONTENT_STEPS.length;
  const contentStepIndex = CONTENT_STEPS.indexOf(currentStep as IkigaiFormStep);
  const progressPercent = currentStep === 'intro' ? 0
    : currentStep === 'result' ? 100
    : Math.round(((contentStepIndex + 1) / totalContentSteps) * 100);

  const currentConfig = IKIGAI_STEPS.find(s => s.step === currentStep);

  const handleAnswerChange = (field: keyof IkigaiAnswers, value: string) => {
    setAnswers(prev => ({ ...prev, [field]: value }));
  };

  const isCurrentStepValid = (): boolean => {
    if (currentStep === 'intro') return true;
    if (!currentConfig) return true;
    return currentConfig.questions.every(q => answers[q.field].trim().length > 0);
  };

  const goNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEP_ORDER.length) {
      setCurrentStep(STEP_ORDER[nextIndex]);
    }
  };

  const goPrev = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEP_ORDER[prevIndex]);
    }
  };

  const handleFinish = () => {
    onComplete(answers);
  };

  // Intro screen
  if (currentStep === 'intro') {
    return (
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-8 text-center">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
              style={{ backgroundColor: 'rgba(141,168,160,0.12)' }}
            >
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <circle cx="14" cy="14" r="13" stroke="#7A9488" strokeWidth="1.5" />
                <circle cx="10" cy="11" r="5" stroke="#7A9488" strokeWidth="1.2" strokeOpacity="0.6" />
                <circle cx="18" cy="11" r="5" stroke="#7A9488" strokeWidth="1.2" strokeOpacity="0.6" />
                <circle cx="10" cy="17" r="5" stroke="#7A9488" strokeWidth="1.2" strokeOpacity="0.6" />
                <circle cx="18" cy="17" r="5" stroke="#7A9488" strokeWidth="1.2" strokeOpacity="0.6" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Ontdek jouw Purpose
            </h2>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              Purpose is het kruispunt van wat jij liefhebt, waar je goed in bent,
              wat de wereld nodig heeft en waarvoor je betaald kunt worden.
              Dit fundament geeft richting aan al je doelen.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-8 text-left">
              {[
                { label: 'Waar hou je van?', color: 'bg-rose-50 border-rose-200 text-rose-700' },
                { label: 'Waar ben je goed in?', color: 'bg-amber-50 border-amber-200 text-amber-700' },
                { label: 'Wat heeft de wereld nodig?', color: 'bg-teal-50 border-teal-200 text-teal-700' },
                { label: 'Waarvoor kun je betaald worden?', color: 'bg-violet-50 border-violet-200 text-violet-700' },
              ].map(item => (
                <div key={item.label} className={`rounded-lg border p-3 text-xs font-medium ${item.color}`}>
                  {item.label}
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-500 mb-6">
              Per onderdeel 3 vragen — neem de tijd om eerlijk te antwoorden.
            </p>

            <div className="flex gap-3 justify-center">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={goNext}
                className="px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#7A9488' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#6B8579'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#7A9488'; }}
              >
                Starten
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Question step
  if (currentConfig && currentStep !== 'result') {
    return (
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Progress bar */}
          <div className="h-1 bg-gray-100">
            <div
              className="h-1 transition-all duration-300"
              style={{ width: `${progressPercent}%`, backgroundColor: '#7A9488' }}
            />
          </div>

          <div className="p-8">
            {/* Step indicator */}
            <div className="flex items-center justify-between mb-6">
              <span className="text-xs text-gray-400">
                Stap {contentStepIndex + 1} van {totalContentSteps}
              </span>
              <div className="flex gap-1.5">
                {CONTENT_STEPS.map((s, i) => (
                  <div
                    key={s}
                    className="w-2 h-2 rounded-full transition-colors"
                    style={{
                      backgroundColor: i <= contentStepIndex ? '#7A9488' : '#E5E7EB',
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Header */}
            <div className={`rounded-lg border p-4 mb-6 ${currentConfig.color} ${currentConfig.borderColor}`}>
              <h3 className={`text-lg font-semibold mb-1 ${currentConfig.textColor}`}>
                {currentConfig.titleNL}
              </h3>
              <p className="text-xs text-gray-600">{currentConfig.subtitleNL}</p>
            </div>

            {/* Questions */}
            <div className="space-y-5">
              {currentConfig.questions.map((q, idx) => (
                <div key={q.field}>
                  <label className="block text-sm font-medium text-gray-800 mb-1.5">
                    {idx + 1}. {q.labelNL}
                  </label>
                  <textarea
                    value={answers[q.field]}
                    onChange={e => handleAnswerChange(q.field, e.target.value)}
                    placeholder={q.placeholderNL}
                    rows={3}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                    style={{ '--tw-ring-color': '#7A9488' } as React.CSSProperties}
                    onFocus={e => { e.currentTarget.style.borderColor = '#7A9488'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(122,148,136,0.2)'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-5 border-t border-gray-100">
              <button
                onClick={goPrev}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ChevronLeft size={16} />
                Terug
              </button>

              <button
                onClick={goNext}
                disabled={!isCurrentStepValid()}
                className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: isCurrentStepValid() ? '#7A9488' : '#9CA3AF' }}
                onMouseEnter={e => { if (isCurrentStepValid()) e.currentTarget.style.backgroundColor = '#6B8579'; }}
                onMouseLeave={e => { if (isCurrentStepValid()) e.currentTarget.style.backgroundColor = '#7A9488'; }}
              >
                {currentStep === 'paid' ? 'Bekijk resultaat' : 'Volgende'}
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Result / confirm step
  if (currentStep === 'result') {
    return (
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="h-1" style={{ backgroundColor: '#7A9488' }} />
          <div className="p-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 text-center">
              Jouw antwoorden zijn klaar
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              Controleer en sla je Purpose op. Je kunt dit altijd later aanpassen.
            </p>

            <div className="space-y-3">
              {IKIGAI_STEPS.map(config => (
                <div key={config.step} className={`rounded-lg border p-4 ${config.color} ${config.borderColor}`}>
                  <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${config.textColor}`}>
                    {config.titleNL}
                  </p>
                  <div className="space-y-1">
                    {config.questions.map(q => (
                      <p key={q.field} className="text-xs text-gray-700 leading-relaxed">
                        <span className="font-medium text-gray-500">{q.labelNL.split('?')[0]}:</span>{' '}
                        {answers[q.field] || <span className="italic text-gray-400">Niet ingevuld</span>}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-8 pt-5 border-t border-gray-100">
              <button
                onClick={goPrev}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ChevronLeft size={16} />
                Terug
              </button>

              <button
                onClick={handleFinish}
                disabled={isSaving}
                className="px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#7A9488' }}
                onMouseEnter={e => { if (!isSaving) e.currentTarget.style.backgroundColor = '#6B8579'; }}
                onMouseLeave={e => { if (!isSaving) e.currentTarget.style.backgroundColor = '#7A9488'; }}
              >
                {isSaving ? 'Opslaan...' : 'Purpose opslaan'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default IkigaiForm;
